/**
 * Kadanza-side creative material, normalized away from the GraFx API shape.
 *
 * Only `project` is emitted: ShopX's backend has no production outputs for
 * digital channels, so the extension works off the user's projects instead.
 * `production_output` stays in the union because `distributions` rows written
 * before the switch still say so and are read back through this type.
 */
export type SourceType = 'production_output' | 'project';

/** What a layout was authored for, as GraFx classifies it. */
export type OutputIntent = 'print' | 'digital_static' | 'digital_animated';

/** One sized variant of a source — the unit that gets distributed. */
export interface SourceLayout {
  id: string;
  name: string;
  /**
   * Unsigned CloudFront URL, snapshotted at request time so background work
   * needs no Kadanza token. Null while GraFx is still rendering the layout's
   * preview, which is the only image a project layout ever has.
   */
  contentUrl: string | null;
  previewUrl: string | null;
  width: number | null;
  height: number | null;
  unitOfMeasurement: string | null;
  outputIntent: OutputIntent | null;
  mimeType: string | null;
  sizeBytes: number | null;
}

export interface Source {
  sourceType: SourceType;
  sourceId: string;
  name: string;
  updatedAt: string | null;
  layouts: SourceLayout[];
}

/**
 * Whether a layout may be distributed, and what to tell the user if not.
 *
 * `blocked` and `warned` are separate because they need opposite treatment:
 * blocked layouts are shown greyed out and cannot be selected, while a warning
 * is a caveat the user is free to accept. Both carry a reason written for a
 * marketer rather than for a developer.
 */
export type LayoutDistributability =
  { status: 'ok' } | { status: 'warned'; reason: string } | { status: 'blocked'; reason: string };

const PRINT_REASON =
  'This is a print layout, and this tool only sends creatives to digital ad channels. Use a digital layout of the same design instead.';

const ANIMATED_REASON =
  'This layout is animated, but GraFx only previews it as a still image — the channel will receive that single frame, not the animation.';

const PENDING_REASON =
  'GraFx has not finished rendering this layout yet. It becomes available to distribute once the preview is ready.';

/**
 * Classifies a layout before a run is submitted.
 *
 * `outputIntent` is authoritative, set in GraFx by whoever authored the layout,
 * which is why this can block rather than only warn. Nothing about the image
 * itself is inspected: every layout is distributed as its preview, and every
 * preview is a JPEG, so the file tells us nothing a print layout and a digital
 * one would not both say. An unset intent is treated as fine — refusing a
 * legitimate layout costs more than the late rejection it would have saved.
 *
 * A missing preview is reported after the intent, so a print layout is told the
 * permanent reason it cannot be distributed rather than a temporary one that
 * would resolve itself.
 */
export function layoutDistributability(layout: SourceLayout): LayoutDistributability {
  if (layout.outputIntent === 'print') return { status: 'blocked', reason: PRINT_REASON };
  if (!layout.contentUrl) return { status: 'blocked', reason: PENDING_REASON };
  if (layout.outputIntent === 'digital_animated') {
    return { status: 'warned', reason: ANIMATED_REASON };
  }
  return { status: 'ok' };
}
